import { ActionIcon, Box, Menu, Text } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { useDeleteClient } from 'src/clients/hooks/useDeleteClient';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { useRemoveFamilyMember } from 'src/clients/hooks/useRemoveFamilyMember';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { SortableColumn } from 'src/common/components/table/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { formatShortDate } from 'src/utils/dateFormatting';

type Props = {
  familyId: string;
};

export function FamilyMembersTable({ familyId }: Props) {
  const { family } = useFamily(familyId);
  const { familyRoles } = useFamilyRoles();
  const {
    deleteClient,
    error: deleteError,
    isLoading: isDeleting,
    reset: resetDelete,
  } = useDeleteClient();
  const {
    removeFamilyMember,
    error: removeError,
    isLoading: isRemoving,
    reset: resetRemove,
  } = useRemoveFamilyMember();
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToRemove, setClientToRemove] = useState<Client | null>(null);

  function handleDeletePress(client: Client) {
    resetDelete();
    setClientToDelete(client);
  }

  async function handleDeleteConfirm() {
    if (!clientToDelete) return;
    try {
      await deleteClient(clientToDelete.id);
      setClientToDelete(null);
    } catch {
      // error surfaced via deleteError
    }
  }

  function handleRemovePress(client: Client) {
    resetRemove();
    setClientToRemove(client);
  }

  async function handleRemoveConfirm() {
    if (!clientToRemove) return;
    try {
      await removeFamilyMember(familyId, clientToRemove.id);
      setClientToRemove(null);
    } catch {
      // error surfaced via removeError
    }
  }

  const roleNameById = new Map(familyRoles.map(role => [role.id, role.name]));
  const roleIdByClientId = new Map(
    (family?.members ?? []).map(member => [member.clientId, member.familyRoleId]),
  );

  const columnsBase: SortableColumn<Client>[] = [
    {
      accessor: 'name',
      title: 'Member',
      render: (client: Client) => (
        <Box>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
          </Text>
          {client.emailAddress && (
            <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
              {client.emailAddress}
            </Text>
          )}
        </Box>
      ),
    },
    {
      accessor: 'role',
      title: 'Role',
      render: (client: Client) => {
        const roleId = roleIdByClientId.get(client.id);
        const roleName = roleId ? roleNameById.get(roleId) : null;
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c={roleName ? 'black' : 'gray.6'}>
            {roleName ?? '—'}
          </Text>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      render: (client: Client) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {formatShortDate(client.createdAt)}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: Client) => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={20} color="var(--mantine-color-black)" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleRemovePress(client)}>Remove from family</Menu.Item>
              <Menu.Item color="red" onClick={() => handleDeletePress(client)}>
                Delete client
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  return (
    <>
      <SimpleTable<Client>
        resource="clients"
        filters={{ familyId }}
        pageSize={9999}
        columns={columnsBase}
        variant="new-flow"
        enableSearch={false}
      />
      <ConfirmDeleteModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete client"
        message={`Are you sure you want to delete ${formatName(
          clientToDelete?.firstName,
          clientToDelete?.lastName,
          NameFormat.NATURAL,
        )}? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />
      <ConfirmDeleteModal
        opened={!!clientToRemove}
        onClose={() => setClientToRemove(null)}
        onConfirm={handleRemoveConfirm}
        title="Remove from family"
        confirmLabel="Remove from family"
        message={`Remove ${formatName(
          clientToRemove?.firstName,
          clientToRemove?.lastName,
          NameFormat.NATURAL,
        )} from ${family?.name ?? 'this family'}? They'll stay a client but will no longer belong to the family.`}
        error={removeError}
        isLoading={isRemoving}
      />
    </>
  );
}
